/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS } from '../programs';
import {
  getFeeRoutingMatrixDecoder,
  getFeeRoutingMatrixEncoder,
  getRehypeBeneficiaryInputDecoder,
  getRehypeBeneficiaryInputEncoder,
  type FeeRoutingMatrix,
  type FeeRoutingMatrixArgs,
  type RehypeBeneficiaryInput,
  type RehypeBeneficiaryInputArgs,
} from '../types';

export const INITIALIZE_REHYPE_DISCRIMINATOR = new Uint8Array([
  52, 224, 165, 199, 71, 188, 192, 155,
]);

export function getInitializeRehypeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_REHYPE_DISCRIMINATOR,
  );
}

export type InitializeRehypeInstruction<
  TProgram extends string = typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountQuoteMint extends string | AccountMeta<string> = string,
  TAccountHookProgram extends string | AccountMeta<string> = string,
  TAccountInitializerProgram extends string | AccountMeta<string> =
    '4h3Dqyo5qmteJoMxXt3tdtfXELDB6pdRTPU9mWruiKp1',
  TAccountRehypeState extends string | AccountMeta<string> = string,
  TAccountRehypeAuthority extends string | AccountMeta<string> = string,
  TAccountSettlementSigner extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountBaseMint extends string
        ? ReadonlySignerAccount<TAccountBaseMint> &
            AccountSignerMeta<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountQuoteMint extends string
        ? ReadonlyAccount<TAccountQuoteMint>
        : TAccountQuoteMint,
      TAccountHookProgram extends string
        ? ReadonlyAccount<TAccountHookProgram>
        : TAccountHookProgram,
      TAccountInitializerProgram extends string
        ? ReadonlyAccount<TAccountInitializerProgram>
        : TAccountInitializerProgram,
      TAccountRehypeState extends string
        ? WritableAccount<TAccountRehypeState>
        : TAccountRehypeState,
      TAccountRehypeAuthority extends string
        ? ReadonlyAccount<TAccountRehypeAuthority>
        : TAccountRehypeAuthority,
      TAccountSettlementSigner extends string
        ? ReadonlyAccount<TAccountSettlementSigner>
        : TAccountSettlementSigner,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeRehypeInstructionData = {
  discriminator: ReadonlyUint8Array;
  namespace: Address;
  launchId: ReadonlyUint8Array;
  buybackDestination: Address;
  settlementAuthority: Address;
  routingMode: number;
  feeRouting: FeeRoutingMatrix;
  beneficiaries: Array<RehypeBeneficiaryInput>;
};

export type InitializeRehypeInstructionDataArgs = {
  namespace: Address;
  launchId: ReadonlyUint8Array;
  buybackDestination: Address;
  settlementAuthority: Address;
  routingMode: number;
  feeRouting: FeeRoutingMatrixArgs;
  beneficiaries: Array<RehypeBeneficiaryInputArgs>;
};

export function getInitializeRehypeInstructionDataEncoder(): Encoder<InitializeRehypeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['namespace', getAddressEncoder()],
      ['launchId', fixEncoderSize(getBytesEncoder(), 32)],
      ['buybackDestination', getAddressEncoder()],
      ['settlementAuthority', getAddressEncoder()],
      ['routingMode', getU8Encoder()],
      ['feeRouting', getFeeRoutingMatrixEncoder()],
      ['beneficiaries', getArrayEncoder(getRehypeBeneficiaryInputEncoder())],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_REHYPE_DISCRIMINATOR }),
  );
}

export function getInitializeRehypeInstructionDataDecoder(): Decoder<InitializeRehypeInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['namespace', getAddressDecoder()],
    ['launchId', fixDecoderSize(getBytesDecoder(), 32)],
    ['buybackDestination', getAddressDecoder()],
    ['settlementAuthority', getAddressDecoder()],
    ['routingMode', getU8Decoder()],
    ['feeRouting', getFeeRoutingMatrixDecoder()],
    ['beneficiaries', getArrayDecoder(getRehypeBeneficiaryInputDecoder())],
  ]);
}

export function getInitializeRehypeInstructionDataCodec(): Codec<
  InitializeRehypeInstructionDataArgs,
  InitializeRehypeInstructionData
> {
  return combineCodec(
    getInitializeRehypeInstructionDataEncoder(),
    getInitializeRehypeInstructionDataDecoder(),
  );
}

export type InitializeRehypeAsyncInput<
  TAccountPayer extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountHookProgram extends string = string,
  TAccountInitializerProgram extends string = string,
  TAccountRehypeState extends string = string,
  TAccountRehypeAuthority extends string = string,
  TAccountSettlementSigner extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  /** Future launch mint. Its signature prevents another caller from claiming this state PDA. */
  baseMint: TransactionSigner<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  hookProgram: Address<TAccountHookProgram>;
  initializerProgram?: Address<TAccountInitializerProgram>;
  rehypeState?: Address<TAccountRehypeState>;
  rehypeAuthority?: Address<TAccountRehypeAuthority>;
  settlementSigner?: Address<TAccountSettlementSigner>;
  systemProgram?: Address<TAccountSystemProgram>;
  namespace: InitializeRehypeInstructionDataArgs['namespace'];
  launchId: InitializeRehypeInstructionDataArgs['launchId'];
  buybackDestination: InitializeRehypeInstructionDataArgs['buybackDestination'];
  settlementAuthority: InitializeRehypeInstructionDataArgs['settlementAuthority'];
  routingMode: InitializeRehypeInstructionDataArgs['routingMode'];
  feeRouting: InitializeRehypeInstructionDataArgs['feeRouting'];
  beneficiaries: InitializeRehypeInstructionDataArgs['beneficiaries'];
};

export async function getInitializeRehypeInstructionAsync<
  TAccountPayer extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountHookProgram extends string,
  TAccountInitializerProgram extends string,
  TAccountRehypeState extends string,
  TAccountRehypeAuthority extends string,
  TAccountSettlementSigner extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
>(
  input: InitializeRehypeAsyncInput<
    TAccountPayer,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountHookProgram,
    TAccountInitializerProgram,
    TAccountRehypeState,
    TAccountRehypeAuthority,
    TAccountSettlementSigner,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeRehypeInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountHookProgram,
    TAccountInitializerProgram,
    TAccountRehypeState,
    TAccountRehypeAuthority,
    TAccountSettlementSigner,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
    initializerProgram: {
      value: input.initializerProgram ?? null,
      isWritable: false,
    },
    rehypeState: { value: input.rehypeState ?? null, isWritable: true },
    rehypeAuthority: {
      value: input.rehypeAuthority ?? null,
      isWritable: false,
    },
    settlementSigner: {
      value: input.settlementSigner ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.initializerProgram.value) {
    accounts.initializerProgram.value =
      '4h3Dqyo5qmteJoMxXt3tdtfXELDB6pdRTPU9mWruiKp1' as Address<'4h3Dqyo5qmteJoMxXt3tdtfXELDB6pdRTPU9mWruiKp1'>;
  }
  if (!accounts.rehypeState.value) {
    accounts.rehypeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 104, 121, 112, 101, 95, 115, 116, 97, 116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.rehypeAuthority.value) {
    accounts.rehypeAuthority.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 104, 121, 112, 101, 95, 97, 117, 116, 104, 111, 114, 105,
            116, 121,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'rehypeState',
            accounts.rehypeState.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.settlementSigner.value) {
    accounts.settlementSigner.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 101, 116, 116, 108, 101, 109, 101, 110, 116, 95, 115, 105, 103,
            110, 101, 114,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'rehypeState',
            accounts.rehypeState.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('hookProgram', accounts.hookProgram),
      getAccountMeta('initializerProgram', accounts.initializerProgram),
      getAccountMeta('rehypeState', accounts.rehypeState),
      getAccountMeta('rehypeAuthority', accounts.rehypeAuthority),
      getAccountMeta('settlementSigner', accounts.settlementSigner),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeRehypeInstructionDataEncoder().encode(
      args as InitializeRehypeInstructionDataArgs,
    ),
    programAddress,
  } as InitializeRehypeInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountHookProgram,
    TAccountInitializerProgram,
    TAccountRehypeState,
    TAccountRehypeAuthority,
    TAccountSettlementSigner,
    TAccountSystemProgram
  >);
}

export type InitializeRehypeInput<
  TAccountPayer extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountHookProgram extends string = string,
  TAccountInitializerProgram extends string = string,
  TAccountRehypeState extends string = string,
  TAccountRehypeAuthority extends string = string,
  TAccountSettlementSigner extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  /** Future launch mint. Its signature prevents another caller from claiming this state PDA. */
  baseMint: TransactionSigner<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  hookProgram: Address<TAccountHookProgram>;
  initializerProgram?: Address<TAccountInitializerProgram>;
  rehypeState: Address<TAccountRehypeState>;
  rehypeAuthority: Address<TAccountRehypeAuthority>;
  settlementSigner: Address<TAccountSettlementSigner>;
  systemProgram?: Address<TAccountSystemProgram>;
  namespace: InitializeRehypeInstructionDataArgs['namespace'];
  launchId: InitializeRehypeInstructionDataArgs['launchId'];
  buybackDestination: InitializeRehypeInstructionDataArgs['buybackDestination'];
  settlementAuthority: InitializeRehypeInstructionDataArgs['settlementAuthority'];
  routingMode: InitializeRehypeInstructionDataArgs['routingMode'];
  feeRouting: InitializeRehypeInstructionDataArgs['feeRouting'];
  beneficiaries: InitializeRehypeInstructionDataArgs['beneficiaries'];
};

export function getInitializeRehypeInstruction<
  TAccountPayer extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountHookProgram extends string,
  TAccountInitializerProgram extends string,
  TAccountRehypeState extends string,
  TAccountRehypeAuthority extends string,
  TAccountSettlementSigner extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
>(
  input: InitializeRehypeInput<
    TAccountPayer,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountHookProgram,
    TAccountInitializerProgram,
    TAccountRehypeState,
    TAccountRehypeAuthority,
    TAccountSettlementSigner,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeRehypeInstruction<
  TProgramAddress,
  TAccountPayer,
  TAccountBaseMint,
  TAccountQuoteMint,
  TAccountHookProgram,
  TAccountInitializerProgram,
  TAccountRehypeState,
  TAccountRehypeAuthority,
  TAccountSettlementSigner,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
    initializerProgram: {
      value: input.initializerProgram ?? null,
      isWritable: false,
    },
    rehypeState: { value: input.rehypeState ?? null, isWritable: true },
    rehypeAuthority: {
      value: input.rehypeAuthority ?? null,
      isWritable: false,
    },
    settlementSigner: {
      value: input.settlementSigner ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.initializerProgram.value) {
    accounts.initializerProgram.value =
      '4h3Dqyo5qmteJoMxXt3tdtfXELDB6pdRTPU9mWruiKp1' as Address<'4h3Dqyo5qmteJoMxXt3tdtfXELDB6pdRTPU9mWruiKp1'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('hookProgram', accounts.hookProgram),
      getAccountMeta('initializerProgram', accounts.initializerProgram),
      getAccountMeta('rehypeState', accounts.rehypeState),
      getAccountMeta('rehypeAuthority', accounts.rehypeAuthority),
      getAccountMeta('settlementSigner', accounts.settlementSigner),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeRehypeInstructionDataEncoder().encode(
      args as InitializeRehypeInstructionDataArgs,
    ),
    programAddress,
  } as InitializeRehypeInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountHookProgram,
    TAccountInitializerProgram,
    TAccountRehypeState,
    TAccountRehypeAuthority,
    TAccountSettlementSigner,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeRehypeInstruction<
  TProgram extends string = typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    payer: TAccountMetas[0];
    /** Future launch mint. Its signature prevents another caller from claiming this state PDA. */
    baseMint: TAccountMetas[1];
    quoteMint: TAccountMetas[2];
    hookProgram: TAccountMetas[3];
    initializerProgram: TAccountMetas[4];
    rehypeState: TAccountMetas[5];
    rehypeAuthority: TAccountMetas[6];
    settlementSigner: TAccountMetas[7];
    systemProgram: TAccountMetas[8];
  };
  data: InitializeRehypeInstructionData;
};

export function parseInitializeRehypeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeRehypeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 9,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      payer: getNextAccount(),
      baseMint: getNextAccount(),
      quoteMint: getNextAccount(),
      hookProgram: getNextAccount(),
      initializerProgram: getNextAccount(),
      rehypeState: getNextAccount(),
      rehypeAuthority: getNextAccount(),
      settlementSigner: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeRehypeInstructionDataDecoder().decode(instruction.data),
  };
}
